<template>
  <v-container
    v-if="showReportManager"
    class="ma-0"
    fluid
  >
    <v-col>
      <v-row class="pb-2">
        <v-col>
          <v-row>
            <h2>{{ reportTitle }}</h2>
          </v-row>

          <v-row>
            <p class="caption">
              Create, save, and share reports.
            </p>
          </v-row>
        </v-col>
      </v-row>

      <v-row>
        <v-col
          v-if="showCollectionPicker"
          :class="`col-auto ${clearColor} window-container`"
        >
          <v-row>
            <v-card
              class="elevation-0"
              style="max-width: 300px; min-width: 270px;"
            >
              <v-card-title class="py-2">
                <v-row>
                  <v-col
                    class="col-grow d-flex align-center
"
                    @click.stop="resetSelectedOrganization"
                  >
                    <span class="subtitle-2 font-weight-bold">Networks</span>
                  </v-col>
                </v-row>
              </v-card-title>

              <v-divider />

              <v-card-text class="pa-0">
                <v-col>
                  <v-row class="py-1">
                    <v-col class="col-grow py-1 px-4 d-flex align-center">
                      <span class="font-weight-medium caption">Choose a network.</span>
                    </v-col>

                    <v-col
                      v-if="isInternalUser"
                      class="col-auto mx-3 py-0 d-flex align-center"
                    >
                      <v-row>
                        <v-btn
                          aria-label="Edit Globals"
                          :color="secondaryColor"
                          outlined
                          x-small
                          @click="viewGlobalReports"
                        >
                          <span>Edit Globals</span>
                        </v-btn>
                      </v-row>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-divider />
                  </v-row>

                  <v-row>
                    <v-list
                      dense
                      class="col pt-0"
                    >
                      <v-list-item
                        v-for="(organization, index) in teamsWithCurrentBaseReportName"
                        :key="index"
                        @click.stop="selectOrganization(organization)"
                      >
                        <v-list-item-content>
                          <v-list-item-title>
                            <v-row class="justify-space-between align-center flex-nowrap">
                              <v-col class="col-grow text-truncate">
                                <span
                                  :class="selectedOrganization && selectedOrganization.associationId === organization.associationId && selectedOrganization.subId === organization.subId ? 'font-weight-bold' : ''"
                                >
                                  {{ organization.teamName }}
                                </span>
                              </v-col>

                              <v-col class="col-auto">
                                <v-icon
                                  :class="`${selectedOrganization && selectedOrganization.associationId === organization.associationId && selectedOrganization.subId === organization.subId ? '' : `${clearColor}--text`}`"
                                >
                                  chevron_right
                                </v-icon>
                              </v-col>
                            </v-row>
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-row>
                </v-col>
              </v-card-text>
            </v-card>
          </v-row>
        </v-col>

        <v-col :class="`col-auto border-left-gray ${clearColor} window-container`">
          <v-row>
            <v-card
              class="elevation-0"
              style="max-width: 300px; min-width: 270px;"
            >
              <v-card-title
                v-if="selectedOrganization"
                class="subtitle-2 font-weight-bold px-3 py-2"
              >
                <v-row class="justify-space-between">
                  <v-col
                    class="px-4 col-grow text-truncate"
                    @click.stop="resetSelectedReport"
                  >
                    <span :class="`${secondaryColor}--text`">{{ selectedOrganization.teamName }}</span>
                  </v-col>

                  <v-col class="col-auto">
                    <v-menu
                      offset-y
                    >
                      <template v-slot:activator="{ on }">
                        <v-btn
                          :color="secondaryColor"
                          icon
                          x-small
                          v-on="on"
                        >
                          <v-icon>more_vert</v-icon>
                        </v-btn>
                      </template>

                      <v-list>
                        <v-list-item
                          @click="toggleArchivedReports"
                        >
                          <v-list-item-title>
                            <span>{{ $showArchivedReports ? 'Hide' : 'Show' }} Archived</span>
                          </v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </v-col>
                </v-row>
              </v-card-title>

              <v-divider v-if="selectedOrganization" />

              <v-card-text
                v-if="selectedOrganization"
                class="pa-0"
              >
                <v-col>
                  <v-row class="py-1">
                    <v-col class="col-grow py-1 px-4 d-flex align-center">
                      <span class="font-weight-bold caption">Reports</span>
                    </v-col>

                    <v-col class="col-auto mx-4 py-0 d-flex align-center">
                      <v-row>
                        <v-btn
                          aria-label="Create Report"
                          :color="secondaryColor"
                          outlined
                          x-small
                          @click.stop="createNewReport"
                        >
                          <span>Create</span>
                        </v-btn>
                      </v-row>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-divider />
                  </v-row>

                  <v-row v-if="reportOptionsForSelectedTeam && reportOptionsForSelectedTeam.length">
                    <v-list
                      v-model="reportOptionsForSelectedTeam"
                      dense
                      class="col pt-0"
                    >
                      <draggable v-model="draggedReportOptionsForSelectedTeam">
                        <v-list-item
                          v-for="report in reportOptionsForSelectedTeam"
                          :key="report.docId"
                          active-class="font-weight-bold"
                        >
                          <v-list-item-content>
                            <v-row class="justify-space-between">
                              <v-col class="col-grow">
                                <v-row>
                                  <v-col class="col-auto d-flex pr-0">
                                    <v-icon
                                      :aria-label="`Drag ${report.reportLabel}`"
                                      class="mr-1"
                                      small
                                      title="Drag to Reorder"
                                    >
                                      drag_indicator
                                    </v-icon>
                                  </v-col>

                                  <v-col class="pl-2 ma-auto subtitle-2">
                                    <span
                                      v-if="report.isTemporary"
                                    >
                                      <span :class="getClassesForReportLabel(report)">
                                        {{ report.reportLabel }}
                                      </span>

                                      <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                          <sup>
                                            <v-icon
                                              x-small
                                              class="ml-1"
                                              v-bind="attrs"
                                              v-on="on"
                                            >
                                              emergency
                                            </v-icon>
                                          </sup>
                                        </template>
                                        <span>Unsaved. Save to keep your edits.</span>
                                      </v-tooltip>
                                    </span>

                                    <span v-else>
                                      <span
                                        v-if="report.associationId === 'all-associations'"
                                        title="Applies to All Associations"
                                        :class="`${getClassesForReportLabel(report)} ${notLinkColor}--text`"
                                      >
                                        {{ report.reportLabel }}
                                      </span>

                                      <router-link
                                        v-else
                                        :to="report.href"
                                        title="Go to Report"
                                      >
                                        <span
                                          :class="getClassesForReportLabel(report)"
                                        >
                                          {{ report.reportLabel }}
                                        </span>
                                      </router-link>

                                      <v-tooltip
                                        v-if="showTooltipOnReportLabel(report)"
                                        bottom
                                      >
                                        <template v-slot:activator="{ on, attrs }">
                                          <sup>
                                            <v-icon
                                              x-small
                                              class="ml-1"
                                              v-bind="attrs"
                                              v-on="on"
                                            >
                                              emergency
                                            </v-icon>
                                          </sup>
                                        </template>
                                        <span>Unsaved. Save to keep your edits.</span>
                                      </v-tooltip>
                                    </span>
                                  </v-col>
                                </v-row>
                              </v-col>

                              <v-col
                                v-if="report.isArchived"
                                class="col-auto pr-0"
                              >
                                <v-icon
                                  class="mr-1"
                                  color="secondary"
                                  small
                                >
                                  archive
                                </v-icon>
                              </v-col>

                              <v-col
                                v-if="report.isSharedReport"
                                class="col-auto pr-0"
                              >
                                <v-icon
                                  class="mr-1"
                                  color="secondary"
                                  small
                                >
                                  co_present
                                </v-icon>
                              </v-col>

                              <v-col
                                v-if="report.isTemplate"
                                class="col-auto pr-0"
                              >
                                <v-icon
                                  class="mr-1"
                                  color="secondary"
                                  small
                                >
                                  lock_outline
                                </v-icon>
                              </v-col>

                              <v-col class="col-auto pl-2 midgray--text">
                                <v-icon
                                  class="mr-1"
                                  small
                                  @click.stop="selectOrganizationReport(report)"
                                >
                                  chevron_right
                                </v-icon>
                              </v-col>
                            </v-row>
                          </v-list-item-content>
                        </v-list-item>
                      </draggable>
                    </v-list>
                  </v-row>
                </v-col>
              </v-card-text>
            </v-card>
          </v-row>
        </v-col>

        <v-col :class="`col-grow pa-0 border-left-gray ${clearColor} window-container`">
          <report-builder
            v-if="selectedOrganization && selectedReport"
            :allow-edits="true"
            :form-name="reportFormName"
            :selected-report-doc-id="selectedReportDocId"
          />
        </v-col>
      </v-row>
    </v-col>
  </v-container>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import draggable from 'vuedraggable'

import { createReport, createReportId } from 'src/report/store/utils/report'
import { copyObject } from '@/utils/objects'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapGetters: mapLayoutGetters } = createNamespacedHelpers('layout')
const { mapState: mapReportState } = createNamespacedHelpers('report')
const { mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'ReportManager',
  components: {
    draggable,
    ReportBuilder: () => import('src/report/components/ReportBuilder'),
  },
  data() {
    return {
      allowTemplateEdits: false,
      reportFormName: 'refFormReportManager',
      showReportTemplateModal: false,
      searchNetworks: false,
      showSearchNetworks: false,
      selectedReportPath: null,
      temporaryReportOptions: [],
    }
  },
  computed: {
    ...mapAuthGetters(['inDarkMode', 'inDevMode', 'isInternalAdminUser', 'isInternalUser', 'userEmail']),
    ...mapLayoutGetters(['clearColor', 'midColor']),
    ...mapReportState({
      $isCreatingReport: (state) => state.isCreatingReport,
      $isSubmittingReportUpdate: (state) => state.isSubmittingReportUpdate,
      $showArchivedReports: (state) => state.showArchivedReports,
      $backupReport: (state) => state.backupReport,
      $selectedReport: (state) => state.selectedReport,
    }),
    ...mapToolsGetters([
      'associationIdAndSubId',
      'currentNav',
      'currentPageName',
      'uniqueReportOptions',
      'globalReports',
      'prepareReport',
      'publishersAsTeams',
      'reportOptionsForSelectedTeam',
      'selectedReportTeam',
    ]),
    ...mapToolsState({
      accessFields: (state) => state.accessFields,
      associationId: (state) => state.associationId,
      reportManager: (state) => state.reportManager,
      selectedIds: (state) => state.selectedIds,
      subId: (state) => state.subId,
    }),
    secondaryColor() {
      return this.midColor
    },
    notLinkColor() {
      return this.inDarkMode ? 'vuetifygray' : 'nearblack'
    },
    query() {
      return this.$route.query
    },
    action() {
      return this.$route.query.action
    },
    showReportManager() {
      return this.$route.query.action !== 'read'
    },
    showCollectionPicker() {
      const { currentPageName } = this
      const collectionRoutes = []

      return collectionRoutes.indexOf(currentPageName) === -1
    },
    selectedOrganization: {
      get() {
        return this.selectedReportTeam
      },
      set(value) {
        if (typeof value === 'object' && value) {
          const { billingCode, teamId } = value
          const selectedIds = (value && { associationId: billingCode, subId: teamId }) || null

          this.$store.commit('tools/selectedIds', selectedIds)

          this.$router.push({
            params: this.$route.params,
            query: { action: 'edit', associationId: billingCode, subId: teamId },
          })
        } else {
          this.$store.commit('tools/selectedIds', null)

          this.$router.push({
            params: this.$route.params,
            query: { action: 'edit' },
          })
        }
      },
    },
    reportOptions: {
      get() {
        return this.uniqueReportOptions
      },
      set(value) {
        this.$store.commit('tools/temporaryReportOptions', value)
      },
    },
    backupReport: {
      get() {
        return this.$backupReport
      },
      set(value) {
        this.$store.commit('report/backupReport', value)
      },
    },
    selectedReport: {
      get() {
        return this.$selectedReport
      },
      set(newValue) {
        this.backupReport = (newValue && copyObject(newValue)) || null

        this.$store.commit('report/selectedReport', newValue)

        // Update URL
        const hasChangedRouteParams = () => {
          const { baseReportName: newBaseReportName, routeName: newRouteName } = newValue
          const { baseReportName: oldBaseReportName, routeName: oldRouteName } = this.$route.params
          return newRouteName !== oldRouteName || newBaseReportName !== oldBaseReportName
        }
        const hasChangedRouteQuery = () => {
          const { associationId: newAssociationId, reportPath: newReportPath, subId: newSubId } = newValue
          const { associationId: oldAssociationId, reportPath: oldReportPath, subId: oldSubId } = this.$route.query
          return newAssociationId !== oldAssociationId || newReportPath !== oldReportPath || newSubId !== oldSubId
        }

        if (this.selectedOrganization && newValue && (hasChangedRouteParams() || hasChangedRouteQuery())) {
          const { billingCode, teamId } = this.selectedOrganization
          const { params, query } = this.$route
          const action = query.action === 'create' && !newValue.isTemporary ? 'edit' : query.action

          this.$router.push({
            params,
            query: { ...query, action: action || 'edit', associationId: billingCode, reportPath: newValue?.reportPath, subId: teamId },
          })
        }
      },
    },
    selectedReportDocId() {
      const { selectedReport } = this
      return selectedReport && (selectedReport.docIdOnParent || selectedReport.docId)
    },
    hasFormChanged() {
      return this.selectedReportDocId && this.hasReportChanged(this.backupReport, this.selectedReport)
    },
    teamsWithCurrentBaseReportName() {
      const currentAccessField = this.accessFields[this.currentPageName]
      return this.publishersAsTeams.filter((publisher) => publisher[currentAccessField])
    },
    totalOrganizationsWithCurrentReport() {
      return this.teamsWithCurrentBaseReportName.length
    },
    reportTitle() {
      return this.currentNav?.title
    },
    draggedReportOptionsForSelectedTeam: {
      get() {
        return this.reportOptionsForSelectedTeam
      },
      set(newArray) {
        this.updateIndices(newArray.map((report, index) => {
          report.index = index
          return report
        }))
      },
    },
  },
  watch: {
    $route(newValue, oldValue) {
      if (oldValue && newValue && oldValue.baseReportName !== newValue.baseReportName) {
        this.resetSelectedOrganization()
      }
    },
    reportOptionsForSelectedTeam: {
      deep: true,
      handler() {
        const { reportPath = 'all-data' } = this.$route.query
        const selectedReport = this.reportOptionsForSelectedTeam.find((report) => report.reportPath === reportPath || report.docId === reportPath)

        if (selectedReport) {
          const { selectedReport: lastSelectedReport } = this
          const hasChangedReport = () => {
            const hasChangedDocId = () => {
              const hasLastAndCurrentSelectedReport = lastSelectedReport && selectedReport
              const hasDocIdOnParentOfLast = !!lastSelectedReport && lastSelectedReport.docIdOnParent
              const hasDocIdOnParentOfCurrent = !!selectedReport && selectedReport.docIdOnParent
              return hasLastAndCurrentSelectedReport && (
                (hasDocIdOnParentOfLast && hasDocIdOnParentOfCurrent && lastSelectedReport.docIdOnParent !== selectedReport.docIdOnParent)
                  || ((!hasDocIdOnParentOfCurrent || !hasDocIdOnParentOfLast) && lastSelectedReport.docId !== selectedReport.docId)
              )
            }
            const isSavingReport = () => lastSelectedReport.docId === selectedReport.docId && this.$isSubmittingReportUpdate
            const isCreatingReport = () => {
              if (this.$isCreatingReport) {
                this.$store.commit('report/isCreatingReport', false)
                return true
              }
              return false
            }
            return isCreatingReport() || hasChangedDocId() || isSavingReport()
          }

          if (!lastSelectedReport || hasChangedReport()) {
            this.selectedReport = copyObject(selectedReport)
          }
        } else if (!reportPath) {
          const defaultReport = this.reportOptionsForSelectedTeam.find((report) => report.reportPath === 'all-data')
          this.selectedReport = copyObject(defaultReport)
        }
      },
    },
    backupReport: {
      deep: true,
      handler() {},
    },
    selectedReport: {
      deep: true,
      handler() {},
    },
  },
  created() {
    if (this.$route.query.action === 'create') {
      this.createNewReport()
    }
  },
  methods: {
    resetSelectedOrganization() {
      this.selectedReport = null
      this.selectedOrganization = null
    },
    selectOrganization(organization) {
      this.selectedOrganization = organization
    },

    resetSelectedReport() {
      this.selectedReport = null
    },
    selectOrganizationReport(report) {
      this.selectedReport = report
    },

    getClassesForReportLabel(report) {
      const docId = report.docIdOnParent || report.docId
      return this.selectedReportDocId === docId ? 'font-weight-bold' : 'font-weight-medium'
    },
    showTooltipOnReportLabel(report) {
      const docId = report.docIdOnParent || report.docId
      return this.selectedReportDocId === docId && this.hasFormChanged
    },

    toggleArchivedReports() {
      this.$store.commit('report/showArchivedReports', !this.$showArchivedReports)
    },

    viewGlobalReports() {
      const allAssociations = {
        billingCode: 'all-associations',
        teamId: 'all-secondary',
      }

      this.selectedOrganization = allAssociations
    },
    createNewReport() {
      const { currentNav, globalReports, inDevMode, selectedOrganization, userEmail } = this
      const isAllAssociation = selectedOrganization && selectedOrganization.billingCode === 'all-associations' && selectedOrganization.teamId === 'all-secondary'
      const scope = isAllAssociation && inDevMode ? 'global' : 'local'
      const ownerId = isAllAssociation && inDevMode ? 'all-users' : userEmail
      const docId = createReportId()

      const newReport = createReport(currentNav, {
        associationId: selectedOrganization.billingCode,
        baseReportName: currentNav.baseReportName,
        columns: [],
        defaultRole: 'viewer',
        docId,
        index: globalReports.length,
        isClientEnforced: false,
        isDeleted: false,
        isEditable: true,
        isMetric: false,
        ownerId,
        parameters: [
          {
            action: 'filterBy',
            propertyName: null,
            operator: null,
            value: null,
          },
        ],
        reportLabel: 'New Report',
        reportPath: docId,
        roles: [
          {
            isArchived: false,
            isDeleted: false,
            isFavorite: false,
            isMetric: false,
            isRevoked: false,
            role: 'owner',
            userId: userEmail,
          },
        ],
        routeName: currentNav.routeName,
        scope,
        shares: [],
        subId: selectedOrganization.teamId,
      })

      newReport.isTemporary = true

      this.$store.commit('report/isCreatingReport', true)

      this.selectedReport = newReport
      this.reportOptions = [newReport]
    },

    hasReportChanged(backupReport, selectedReport) {
      const diffValues = () => {
        const diffMap = [
          () => backupReport.associationId !== selectedReport.associationId,
          () => backupReport.baseReportName !== selectedReport.baseReportName,
          () => backupReport.columns.length !== selectedReport.columns.length,
          // () => backupReport.docId !== selectedReport.docId, // Silenced docId check to avoid issues with temp docIds.
          () => backupReport.index !== selectedReport.index,
          () => backupReport.isClientEnforced !== selectedReport.isClientEnforced,
          () => backupReport.isDeleted !== selectedReport.isDeleted,
          () => backupReport.isMetric !== selectedReport.isMetric,
          () => backupReport.ownerId !== selectedReport.ownerId,
          () => backupReport.parameters.length !== selectedReport.parameters.length,
          () => backupReport.parameters.reduce((result, param, paramIndex) => {
            const isGroupBy = param.action === 'groupBy'
            const matchPropertyNameStrings = (key) => param[key] === selectedReport.parameters[paramIndex][key]
            const matchPropertyNameArrays = (key) => {
              const hasPropertyNameAtIndex = () => param[key] && selectedReport.parameters[paramIndex][key]
              const matchesPropertyNameLength = () => param[key].length === selectedReport.parameters[paramIndex][key].length
              const matchesPropertyNames = () => param[key].reduce((resultOfPropertyName, propertyName, propertyNameIndex) => {
                const matchesPropertyName = () => propertyName === selectedReport.parameters[paramIndex][key][propertyNameIndex]
                return resultOfPropertyName && matchesPropertyName()
              }, true)

              return hasPropertyNameAtIndex() && matchesPropertyNameLength() && matchesPropertyNames()
            }
            const matchPropertyName = () => (isGroupBy ? matchPropertyNameArrays('propertyName') : matchPropertyNameStrings('propertyName'))
            const matchValue = () => (Array.isArray(param.value) ? matchPropertyNameArrays('value') : matchPropertyNameStrings('value'))

            return result
              || param.action !== selectedReport.parameters[paramIndex].action
              || param.operator !== selectedReport.parameters[paramIndex].operator
              || !matchPropertyName()
              || !matchValue()
          }, false),
          () => backupReport.reportLabel !== selectedReport.reportLabel,
          () => backupReport.reportPath !== selectedReport.reportPath,
          () => backupReport.roles.length !== selectedReport.roles.length,
          () => backupReport.roles.reduce((result, role, roleIndex) => result
                || role.isDeleted !== selectedReport.roles[roleIndex].isDeleted
                || role.isRevoked !== selectedReport.roles[roleIndex].isRevoked
                || role.userId !== selectedReport.roles[roleIndex].userId
                || role.role !== selectedReport.roles[roleIndex].role, false),
          () => backupReport.shares.length !== selectedReport.shares.length,
          () => backupReport.shares.reduce((result, share, shareIndex) => result || share !== selectedReport.shares[shareIndex], false),
          () => backupReport.routeName !== selectedReport.routeName,
          () => backupReport.scope !== selectedReport.scope,
          () => backupReport.subId !== selectedReport.subId,
        ]

        return diffMap.reduce((diffResult, diffFunction) => {
          if (diffResult !== true) {
            diffResult = diffFunction()
          }

          return diffResult
        }, false)
      }

      return backupReport && selectedReport && diffValues()
    },

    updateIndices(newIndices) {
      const { query } = this
      const { associationId } = query
      const isAllAssociationId = associationId === 'all-associations'
      const isAllSecondary = query.subId === 'all-secondary'
      const isAllAssociation = isAllAssociationId && isAllSecondary

      if (isAllAssociation) {
        newIndices.forEach((newIndex) => {
          this.$store.dispatch('report/updateReport', newIndex)
        })
      } else {
        newIndices.forEach((newIndex) => {
          this.$store.dispatch('report/updateReport', newIndex)
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

.window-container {
  max-height: calc(100vh - 180px);
  overflow: scroll;
}

.border-left-gray {
  border-left: 1px solid $color-monochrome-midgray !important;
}

.v-input.expanding-search {
  .v-input__slot {
    fieldset {
      border-color: transparent !important;
    }
  }
  &.closed {
    max-width: 45px;
  }
}
</style>
